import { Box, Typography } from '@mui/material'
import { type TFunction } from 'i18next'
import { type MRT_ColumnDef, type MRT_Row } from 'material-react-table'
import DatetimeTypeData from '../../Common/DatetimeTypeData'
import TruncatedText from '../../Common/TruncatedText'
import { type UploadRow } from './interface'
import { ReactElement } from 'react'
import { ColumnFilterType } from '../../../constants/constants'
import UploadManagerCustomFilters from './components/UploadManagerCustomFilters'

export const getUploadManagerColumns = (
    statusColors: Record<string, string>,
    t: TFunction,
): MRT_ColumnDef<UploadRow>[] => [
    {
        header: t('uploadManager.columns.date'),
        accessorKey: 'createdAt',
        id: 'createdAt',
        enableSorting: true,
        size: 160,
        enableColumnFilter: false,
        Header: ({ column }) => (
            <Box sx={{ paddingLeft: '14px' }}>
                <TruncatedText
                    text={column.columnDef.header as string}
                    sx={{ fontWeight: 'inherit' }}
                />
            </Box>
        ),

        Cell: ({ row }: { row: MRT_Row<UploadRow> }) => {
            return (
                <TruncatedText>
                    <DatetimeTypeData
                        dataValue={{
                            datetime: row.original?.createdAt,
                        }}
                        fontSize="14px"
                        showTimezone={false}
                    />
                </TruncatedText>
            )
        },
    },

    {
        accessorKey: 'userEmail',
        header: t('uploadManager.columns.user'),
        size: 160,
        enableColumnFilter: false,
        muiTableBodyCellProps: { sx: { padding: '0 10px 0px 2px !important' } },
        Header: ({ column }) => (
            <TruncatedText
                text={column.columnDef.header as string}
                sx={{ fontWeight: 'inherit' }}
            />
        ),

        Cell: ({ cell }) => <TruncatedText text={cell.getValue<string>()} />,
    },
    {
        accessorKey: 'templateName',
        header: t('uploadManager.columns.type'),
        meta: {
            filterType: ColumnFilterType.Upload_Manager_type,
        },
        muiTableBodyCellProps: { sx: { padding: '0 !important' } },
        Header: ({ column }) => (
            <TruncatedText
                text={column.columnDef.header as string}
                sx={{ fontWeight: 'inherit' }}
            />
        ),

        size: 160,
        Cell: ({ cell }) => <TruncatedText text={cell.getValue<string>()} />,
        enableColumnFilter: true,
        Filter: (props: any): ReactElement => (
            <UploadManagerCustomFilters
                {...props}
                templateId={''}
                fetchAllOrgsForOrgFilter={false}
            />
        ),
    },
    {
        accessorKey: 'fileName',
        header: t('uploadManager.columns.fileName'),
        muiTableBodyCellProps: { sx: { padding: '0 10px 0px 2px !important' } },
        enableColumnFilter: false,
        Header: ({ column }) => (
            <TruncatedText
                text={column.columnDef.header as string}
                sx={{ fontWeight: 'inherit' }}
            />
        ),

        size: 160,
        Cell: ({ cell }) => <TruncatedText text={cell.getValue<string>()} />,
    },
    {
        accessorKey: 'status',
        header: t('uploadManager.columns.status'),
        muiTableBodyCellProps: { sx: { padding: '0 10px 0px 2px !important' } },
        size: 140,
        enableColumnFilter: false,
        Header: ({ column }) => (
            <TruncatedText
                text={column.columnDef.header as string}
                sx={{ fontWeight: 'inherit' }}
            />
        ),
        Cell: ({ cell, row }) => {
            const originalStatus = cell.getValue<string>() || ''
            const { totalRows, processedRows } = row.original as UploadRow
            let displayStatus = originalStatus

            if (
                processedRows !== totalRows &&
                originalStatus.toUpperCase() !== 'PENDING' &&
                originalStatus.toUpperCase() !== 'PROCESSING'
            ) {
                displayStatus = 'FAILED'
            }

            const convertedStatus = displayStatus.toUpperCase()
            const color = statusColors[displayStatus]
            return (
                <TruncatedText
                    text={t(`uploadManager.status.${convertedStatus}`)}
                    sx={{
                        color,
                        fontWeight: 400,
                        textTransform: 'capitalize',
                    }}
                />
            )
        },
    },
    {
        accessorKey: 'progress',
        header: t('uploadManager.columns.progress'),
        size: 220,
        enableSorting: false,
        enableColumnFilter: false,
        muiTableBodyCellProps: { sx: { padding: '0 10px 0px 2px !important' } },
        Header: ({ column }) => (
            <TruncatedText
                text={column.columnDef.header as string}
                sx={{ fontWeight: 'inherit' }}
            />
        ),
        Cell: ({ row }) => {
            const processed = row.original.processedRows ?? 0
            const total = row.original.totalRows ?? 0
            const errorCount = row?.original?.errorRows ?? 0
            const successCount = Math.max(0, processed - errorCount)

            const successPercentage =
                total > 0 ? (successCount / total) * 100 : 0
            const errorPercentage = total > 0 ? (errorCount / total) * 100 : 0

            return (
                <Box sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            mb: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                flexGrow: 1,
                                height: 10,
                                borderRadius: 4,
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #D9D9D9',
                                display: 'flex',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${successPercentage}%`,
                                    minWidth: successCount > 0 ? '3px' : 0,
                                    backgroundColor: '#0099A8',
                                    height: '100%',
                                }}
                            />
                            <Box
                                sx={{
                                    width: `${errorPercentage}%`,
                                    minWidth: errorCount > 0 ? '3px' : 0,
                                    backgroundColor: '#ff2828',
                                    height: '100%',
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Text below progress */}
                    <Box sx={{ overflow: 'hidden' }}>
                        <TruncatedText
                            variant="body2"
                            sx={{
                                fontSize: 13,
                                color: 'rgba(0, 0, 0, 0.87);',
                            }}
                        >
                            {t(
                                'uploadManager.bulkUpload.fileUploadedSuccessWithCount',
                                {
                                    processed,
                                    total,
                                    errors: errorCount,
                                },
                            )}
                        </TruncatedText>
                    </Box>
                </Box>
            )
        },
    },

    {
        accessorKey: 'fileUrl',
        header: t('uploadManager.columns.downloadUploadedFile'),
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
        muiTableBodyCellProps: { sx: { padding: '0 10px 0px 2px !important' } },
        Header: ({ column }) => (
            <TruncatedText
                text={column.columnDef.header as string}
                sx={{ fontWeight: 'inherit' }}
            />
        ),

        Cell: ({ row }) => {
            const { fileUrl, fileName } = row.original

            return fileUrl ? (
                <TruncatedText
                    tooltipTitle={
                        fileName || t('uploadManager.columns.fileName')
                    }
                >
                    <Typography
                        id={`upload-manager-download-link-${row.original.id}`}
                        component="a"
                        href={fileUrl}
                        target="_self"
                        rel="noopener noreferrer"
                        download={
                            fileName || t('uploadManager.columns.fileName')
                        }
                        sx={{
                            fontSize: '14px',
                            color: '#036670',
                            cursor: 'pointer',
                            textDecoration: 'none',
                        }}
                    >
                        {fileName || t('uploadManager.columns.fileName')}
                    </Typography>
                </TruncatedText>
            ) : (
                <Typography sx={{ color: '#9ca3af' }}>N/A</Typography>
            )
        },
    },

    {
        accessorKey: 'errorFileUrl',
        header: t('uploadManager.columns.errorFileDownload'),
        size: 300,
        enableSorting: false,
        enableColumnFilter: false,
        enablePinning: true,
        muiTableBodyCellProps: { sx: { padding: '0 10px 0px 2px !important' } },
        Header: ({ column }) => (
            <TruncatedText
                text={column.columnDef.header as string}
                sx={{ fontWeight: 'inherit' }}
            />
        ),
        Cell: ({ row }) => {
            const { errorFileUrl, fileName, createdAt } = row.original

            return errorFileUrl ? (
                <TruncatedText
                    tooltipTitle={
                        fileName || t('uploadManager.columns.fileName')
                    }
                >
                    <Typography
                        id={`upload-manager-error-download-link-${row.original.id}`}
                        component="a"
                        href={errorFileUrl}
                        target={
                            errorFileUrl.endsWith('.csv') ? '_self' : '_blank'
                        }
                        rel="noopener noreferrer"
                        aria-label="download error file"
                        download={
                            errorFileUrl.endsWith('.csv')
                                ? `${fileName || 'error_file'}_${createdAt}.csv`
                                : undefined
                        }
                        sx={{
                            fontSize: '14px',
                            color: '#ff2828',
                            textDecoration: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        {fileName || t('uploadManager.columns.fileName')}
                    </Typography>
                </TruncatedText>
            ) : (
                <TruncatedText
                    text={t('uploadManager.bulkUpload.noErrorsInUpload')}
                    sx={{ color: '#9ca3af', fontSize: '14px' }}
                />
            )
        },
    },
]
